import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Card from "../components/Card";
import { Helmet } from 'react-helmet-async';
import { auth, db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { io } from "socket.io-client";

export default function Home() {
  const navigate = useNavigate();
  const [showLegal, setShowLegal] = useState(null); 
  const [unlockedCardNames, setUnlockedCardNames] = useState([]);
  const [allCards, setAllCards] = useState([]); 
  const carouselTrackRef = useRef(null);

  // Initialisiere Socket für den Quick Play Button
  const [socket, setSocket] = useState(null);

  useEffect(() => {
    const newSocket = io();
    setSocket(newSocket);

    // Wenn der Server das Bot-Game erstellt hat, leiten wir zur Lobby/Game weiter
    newSocket.on("room_created", (code) => {
       window.location.href = `/host?code=${code}`; 
    });
    // 1. Hole ALLE Karten dynamisch vom Server (Spezial & Effekte)
    fetch("/api/cards")
      .then(res => res.json())
      .then(data => {
        const validCards = data.filter(c => c && c.name);
        setAllCards(validCards);
      })
      .catch(err => console.error("Could not load cards:", err));

    // 2. Auth-Listener: Synchronisiere mit Firebase ODER LocalStorage
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Spieler ist eingeloggt -> Aus der Cloud laden
        const docRef = doc(db, "users", user.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setUnlockedCardNames(docSnap.data().discoveredCards || []);
        }
      } else {
        // Gast -> Aus dem LocalStorage laden
        const savedCards = JSON.parse(localStorage.getItem("unlocked_cards")) || [];
        if (Array.isArray(savedCards)) setUnlockedCardNames(savedCards);
      }
    });

    return () => {
        newSocket.disconnect();
        unsubscribe();
    };
  }, []);

  const scrollCarousel = (direction) => {
    if (carouselTrackRef.current) {
      const cardWidth = 150 + 30; 
      const scrollAmount = cardWidth * 3; 
      
      carouselTrackRef.current.scrollBy({
        left: direction * scrollAmount,
        behavior: "smooth" 
      });
    }
  };

  return (
    <div style={{ 
      minHeight: "100vh", color: "white", display: "flex", flexDirection: "column", alignItems: "center", fontFamily: "sans-serif",
      backgroundImage: "url('/background.png')", backgroundSize: "cover", backgroundPosition: "center", backgroundAttachment: "fixed" 
    }}>

      <Helmet>
        <title>UN-LOCKED | Free Multiplayer Deck Building Card Game</title>
        <meta name="description" content="Play UN-LOCKED, a fast-paced multiplayer deck building card game. Play online with friends, unlock unique effect cards, and build your perfect deck in your browser." />
        <meta name="keywords" content="Multiplayer Deck Building Game, Browser Card Game, Play with friends online, Uno alternative, Online Card Game free" />
      </Helmet>
      
      {/* CSS FÜR ANIMATIONEN, HOVER & CAROUSEL */}
      <style>{`
        .btn-play { background: linear-gradient(45deg, #00aa00, #00ff00); transition: all 0.2s ease; color: black; }
        .btn-play:hover { transform: scale(1.05); box-shadow: 0 0 20px rgba(0, 255, 0, 0.6); }
        .btn-bot { background: linear-gradient(45deg, #ff8800, #ffcc00); transition: all 0.2s ease; color: black; }
        .btn-bot:hover { transform: scale(1.05); box-shadow: 0 0 20px rgba(255, 204, 0, 0.6); }
        .btn-secondary { background-color: rgba(68, 68, 68, 0.8); backdrop-filter: blur(5px); transition: all 0.2s ease; }
        .btn-secondary:hover { background-color: rgba(100, 100, 100, 0.9); transform: scale(1.05); }

        .carousel-wrapper {
          width: 100%; max-width: 1000px; position: relative;
          mask-image: linear-gradient(to right, transparent 0%, black 15%, black 85%, transparent 100%);
          -webkit-mask-image: linear-gradient(to right, transparent 0%, black 15%, black 85%, transparent 100%);
        }

        .carousel-track { 
          display: flex; gap: 30px; padding: 60px 80px 100px 80px; 
          overflow-x: auto; scroll-snap-type: x mandatory; scrollbar-width: none;
        }
        .carousel-track::-webkit-scrollbar { display: none; }

        .carousel-btn {
          position: absolute; top: 150px; background-color: rgba(17,17,17,0.9); border: 2px solid #555; color: white;
          width: 50px; height: 50px; border-radius: 50%; font-size: 1.5em; cursor: pointer;
          display: flex; justify-content: center; align-items: center; z-index: 100; transition: all 0.2s ease; line-height: 0;
        }
        .carousel-btn:hover { background-color: gold; border-color: white; color: black; transform: scale(1.1); }
        .carousel-btn.left { left: 10px; }
        .carousel-btn.right { right: 10px; }

        .card-container { position: relative; width: 150px; height: 230px; cursor: pointer; flex: 0 0 auto; scroll-snap-align: center; }
        .card-wrapper { transition: transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275); width: 100%; height: 100%; display: flex; justify-content: center; align-items: center; }
        
        .card-info { 
          position: absolute; bottom: -20px; left: 50%; transform: translateX(-50%); width: 180px; 
          background: rgba(17, 17, 17, 0.95); border: 2px solid #555; border-radius: 10px; padding: 10px; 
          opacity: 0; pointer-events: none; transition: all 0.3s ease; text-align: center; z-index: 10; box-shadow: 0 10px 20px rgba(0,0,0,0.8);
        }
        
        .card-container:hover .card-wrapper { transform: translateY(-20px) scale(1.05); }
        .card-container:hover .card-info { opacity: 1; bottom: -90px; pointer-events: auto; }
      `}</style>

      {/* HEADER */}
      <div style={{ textAlign: "center", marginTop: "80px", marginBottom: "50px", padding: "0 20px" }}>
        <h1 style={{ color: "white", fontSize: "4.5em", margin: "0 0 10px 0", fontWeight: "900", textShadow: "0 5px 15px rgba(0,0,0,0.8)" }}>
          UN-LOCKED
        </h1>
        {/* SEO Text: Hier lesen Google-Bots mit! */}
        <h2 style={{ color: "#aaa", fontSize: "1.4em", margin: "0 auto", fontWeight: "bold", maxWidth: "800px", lineHeight: "1.4", textShadow: "0 2px 5px rgba(0,0,0,0.8)" }}>
          A free online Multiplayer Deck Building Card Game. Unlock powerful Effects, sabotage your Friends, and survive the chaos!
        </h2>
      </div>

      {/* NAVIGATION BUTTONS */}
      <div style={{ display: "flex", gap: "20px", marginBottom: "60px", flexWrap: "wrap", justifyContent: "center" }}>
        {/* 🚀 NEU: QUICK PLAY BUTTON (Der Conversion-Boost!) */}
        <button className="btn-bot" onClick={() => {
            // Leitet weiter und sagt der Lobby-Seite, was sie tun soll
            navigate("/host?action=bot");
        }} style={{ padding: "18px 40px", fontSize: "1.4em", border: "none", borderRadius: "12px", cursor: "pointer", fontWeight: "bold", display: "flex", alignItems: "center", gap: "10px" }}>
          🤖 Play vs Bot (Instant)
        </button>

        <button className="btn-play" onClick={() => navigate("/host")} style={{ padding: "18px 40px", fontSize: "1.4em", border: "none", borderRadius: "12px", cursor: "pointer", fontWeight: "bold" }}>
          👥 Host / Join Game
        </button>

        <button className="btn-secondary" onClick={() => navigate("/rules")} style={{ padding: "18px 30px", fontSize: "1.2em", color: "white", border: "2px solid #666", borderRadius: "12px", cursor: "pointer", fontWeight: "bold" }}>
           Rules & Cards
        </button>
      </div>

      {/* CAROUSEL */}
      <div style={{ width: "100%", borderTop: "2px solid rgba(255,255,255,0.1)", backgroundColor: "rgba(0,0,0,0.3)", backdropFilter: "blur(5px)", display: "flex", flexDirection: "column", alignItems: "center", paddingBottom: "50px", flex: 1 }}>
        <h3 style={{ color: "gold", marginTop: "30px", fontSize: "1.8em", letterSpacing: "2px", textTransform: "uppercase" }}>
          Your Collection ({unlockedCardNames.length}/{allCards.length || "?"})
        </h3>
        
        <div className="carousel-wrapper">
          <button className="carousel-btn left" onClick={() => scrollCarousel(-1)}>‹</button>
          <button className="carousel-btn right" onClick={() => scrollCarousel(1)}>›</button>

          <div className="carousel-track" ref={carouselTrackRef}>
            {allCards.map((card, index) => {
              const isUnlocked = unlockedCardNames.some(item => 
                item === card.name || (item && item.name === card.name)
              );

              return (
                <div key={card.id || index} className="card-container">
                  <div className="card-wrapper" style={{ 
                    filter: isUnlocked ? "none" : "brightness(0) invert(0.2) blur(2px)",
                    opacity: isUnlocked ? 1 : 0.6
                  }}>
                    <Card card={card} disabled={true} />
                  </div>
                  
                  {!isUnlocked && (
                    <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", fontSize: "3em", pointerEvents: "none" }}>
                      🔒
                    </div>
                  )}

                  <div className="card-info" style={{ borderColor: isUnlocked ? (card.color === "special" ? "#ff00ff" : "gold") : "#444" }}>
                    <b style={{ display: "block", color: isUnlocked ? "white" : "#888", fontSize: "1.1em", marginBottom: "5px" }}>
                      {isUnlocked ? card.name : "????"}
                    </b>
                    <span style={{ fontSize: "0.85em", color: isUnlocked ? "#aaa" : "#555", lineHeight: "1.3" }}>
                      {isUnlocked ? card.description : "Keep playing to unlock this secret card..."}
                    </span>
                  </div>
                </div>
              );
            })}

            {allCards.length === 0 && (
              <div style={{ color: "#888", fontStyle: "italic", padding: "20px" }}>Loading Collection...</div>
            )}
          </div>
        </div>
      </div>

      {/* FOOTER */}
      <div style={{ marginTop: "auto", padding: "20px", display: "flex", gap: "30px", fontSize: "0.9em", color: "#888", borderTop: "1px solid rgba(255,255,255,0.1)", width: "100%", justifyContent: "center", backdropFilter: "blur(5px)" }}>
        <span style={{ cursor: "pointer", transition: "color 0.2s" }} onMouseEnter={e => e.target.style.color="white"} onMouseLeave={e => e.target.style.color="#888"} onClick={() => setShowLegal("impressum")}>Legal Notice</span>
        <span style={{ cursor: "pointer", transition: "color 0.2s" }} onMouseEnter={e => e.target.style.color="white"} onMouseLeave={e => e.target.style.color="#888"} onClick={() => setShowLegal("datenschutz")}>Privacy Policy</span>
        <a href="https://buymeacoffee.com/unlockedgame" target="_blank" rel="noopener noreferrer" style={{ transition: "transform 0.2s", display: "inline-block" }}>
          <img src="https://cdn.buymeacoffee.com/buttons/v2/default-yellow.png" alt="Buy Me A Coffee" style={{ height: "50px", width: "200px" }} />
        </a>
      </div>

      {/* LEGAL MODALS */}
      {showLegal && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", backgroundColor: "rgba(0,0,0,0.8)", zIndex: 9999, display: "flex", justifyContent: "center", alignItems: "center" }} onClick={() => setShowLegal(null)}>
          <div style={{ backgroundColor: "#222", padding: "40px", borderRadius: "15px", border: "2px solid gold", maxWidth: "600px", color: "white", textAlign: "left" }} onClick={e => e.stopPropagation()}>
            <h2 style={{ color: "gold", marginTop: 0 }}>{showLegal === "impressum" ? "Legal Notice" : "Privacy Policy"}</h2>
            {showLegal === "impressum" && ( 
              <div style={{ lineHeight: "1.6", color: "#ccc" }}> 
                <p><strong>Information according to § 5 DDG:</strong></p> 
                <p>Jan Hölzle<br/>Zollhofgarten 8<br/>69115 Heidelberg<br/>Germany</p> 
                <p><strong>Contact:</strong><br/>Email: hoelzlejan@gmail.com</p>
              </div> 
            )} 
            {showLegal === "datenschutz" && ( 
              <div style={{ lineHeight: "1.6", color: "#ccc" }}> 
                <p><strong>1. Storage of IP Addresses</strong><br/>To provide the multiplayer functionality (WebSockets), your IP address is temporarily processed on our server (hosted by Strato). This is technically necessary (Art. 6 Para. 1 lit. f GDPR) and will not be permanently stored after the session ends.</p> 
                <p><strong>2. Local Storage & Cookies</strong><br/>We use your browser's Local Storage to save your game progress and session data. We only use Google Analytics with your explicit consent via our cookie banner.</p> 
              </div> 
            )}
            <button onClick={() => setShowLegal(null)} style={{ marginTop: "20px", padding: "10px 20px", backgroundColor: "gold", color: "black", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "bold" }}>Close</button>
          </div>
        </div>
      )}

    </div>
  );
}